import type { ApiClient } from './client';
import type { AuthSession, Operator, OperatorStatus } from '../store/auth';

interface OperatorPayload {
  id: string;
  tenant_id: string;
  email: string;
  name: string;
  role: string;
  avatar_url?: string | null;
  // ANX-036: предпочитаемый язык; отсутствует/"" → «язык браузера».
  locale?: string | null;
  // ANX-071: выбранный статус присутствия (online/away/busy).
  presence_status?: string | null;
}

interface AuthResponse {
  access: string;
  refresh: string;
  expires_in: number;
  operator: OperatorPayload;
}

// LoginResponse — расширенный ответ /auth/login. ANX-013: вместо
// access/refresh может прийти MFA-challenge или enrollment-requirement.
interface LoginResponse extends Partial<AuthResponse> {
  requires_mfa?: boolean;
  mfa_session?: string;
  requires_enrollment?: boolean;
  enrollment_session?: string;
}

export type LoginResult =
  | { kind: 'session'; session: AuthSession }
  | { kind: 'mfa'; mfaSession: string }
  | { kind: 'enrollment'; enrollmentSession: string };

function toOperator(p: OperatorPayload): Operator {
  const op: Operator = {
    id: p.id,
    tenantId: p.tenant_id,
    email: p.email,
    name: p.name,
    role: p.role,
  };
  if (p.avatar_url) op.avatarUrl = p.avatar_url;
  if (p.locale) op.locale = p.locale;
  if (p.presence_status === 'online' || p.presence_status === 'away' || p.presence_status === 'busy') {
    op.presenceStatus = p.presence_status;
  }
  return op;
}

function toSession(r: AuthResponse): AuthSession {
  return {
    access: r.access,
    refresh: r.refresh,
    accessExpiresAt: Date.now() + r.expires_in * 1000,
    operator: toOperator(r.operator),
  };
}

export interface LoginInput {
  email: string;
  password: string;
  tenantSlug: string;
}

// ===== Password reset (ANX-102) ===========================================
// Публичные эндпойнты (auth:false). Запрос сброса идемпотентно отвечает 204
// независимо от существования учётки (анти-enumeration), поэтому resolve ≠
// «email найден». Подтверждение 404 = токен неверен/истёк.

export interface PasswordResetRequestInput {
  email: string;
  tenantSlug: string;
}

export async function requestPasswordReset(
  client: ApiClient,
  input: PasswordResetRequestInput,
): Promise<void> {
  await client.request<undefined>('/api/v1/password_resets', {
    method: 'POST',
    auth: false,
    json: { email: input.email, tenant_slug: input.tenantSlug },
  });
}

export async function confirmPasswordReset(
  client: ApiClient,
  input: { token: string; password: string },
): Promise<void> {
  await client.request<undefined>(
    `/api/v1/password_resets/${encodeURIComponent(input.token)}`,
    { method: 'POST', auth: false, json: { password: input.password } },
  );
}

export async function login(client: ApiClient, input: LoginInput): Promise<LoginResult> {
  const raw = await client.request<LoginResponse>('/api/v1/auth/login', {
    method: 'POST',
    auth: false,
    json: {
      email: input.email,
      password: input.password,
      tenant_slug: input.tenantSlug,
    },
  });
  if (raw.requires_mfa && raw.mfa_session) {
    return { kind: 'mfa', mfaSession: raw.mfa_session };
  }
  if (raw.requires_enrollment && raw.enrollment_session) {
    return { kind: 'enrollment', enrollmentSession: raw.enrollment_session };
  }
  // Здесь access/refresh должны быть — backend всегда возвращает один из трёх ответов.
  return { kind: 'session', session: toSession(raw as AuthResponse) };
}

// ===== Passkey MFA flow =====================================================

export async function beginPasskeyLogin(
  client: ApiClient,
  mfaSession: string,
): Promise<{ options: unknown; sessionToken: string }> {
  const raw = await client.request<{ options: unknown; session_token: string }>(
    '/api/v1/auth/passkey/begin',
    { method: 'POST', auth: false, json: { mfa_session: mfaSession } },
  );
  return { options: raw.options, sessionToken: raw.session_token };
}

export async function finishPasskeyLogin(
  client: ApiClient,
  input: { sessionToken: string; assertion: unknown },
): Promise<AuthSession> {
  const raw = await client.request<AuthResponse>('/api/v1/auth/passkey/finish', {
    method: 'POST',
    auth: false,
    json: { session_token: input.sessionToken, assertion: input.assertion },
  });
  return toSession(raw);
}

export async function loginWithRecoveryCode(
  client: ApiClient,
  input: { mfaSession: string; code: string },
): Promise<AuthSession> {
  const raw = await client.request<AuthResponse>('/api/v1/auth/recovery', {
    method: 'POST',
    auth: false,
    json: { mfa_session: input.mfaSession, code: input.code },
  });
  return toSession(raw);
}

// ===== Forced enrollment (если tenant policy require_mfa = true) ============

export async function beginEnrollmentPasskey(
  client: ApiClient,
  enrollmentSession: string,
): Promise<{ options: unknown; sessionToken: string }> {
  const raw = await client.request<{ options: unknown; session_token: string }>(
    '/api/v1/auth/enrollment/begin-register',
    { method: 'POST', auth: false, json: { enrollment_session: enrollmentSession } },
  );
  return { options: raw.options, sessionToken: raw.session_token };
}

export async function finishEnrollmentPasskey(
  client: ApiClient,
  input: {
    enrollmentSession: string;
    sessionToken: string;
    name: string;
    attestation: unknown;
  },
): Promise<{ session: AuthSession; recoveryCodes: string[] }> {
  const raw = await client.request<AuthResponse & { recovery_codes: string[] }>(
    '/api/v1/auth/enrollment/finish-register',
    {
      method: 'POST',
      auth: false,
      json: {
        enrollment_session: input.enrollmentSession,
        session_token: input.sessionToken,
        name: input.name,
        attestation: input.attestation,
      },
    },
  );
  return { session: toSession(raw), recoveryCodes: raw.recovery_codes };
}

export async function refreshSession(
  client: ApiClient,
  refresh: string,
): Promise<AuthSession> {
  const raw = await client.request<AuthResponse>('/api/v1/auth/refresh', {
    method: 'POST',
    auth: false,
    json: { refresh },
  });
  return toSession(raw);
}

export async function logout(client: ApiClient): Promise<void> {
  await client.request<undefined>('/api/v1/auth/logout', { method: 'POST' });
}

export async function fetchMe(client: ApiClient): Promise<Operator> {
  const raw = await client.request<{ operator: OperatorPayload }>('/api/v1/me');
  return toOperator(raw.operator);
}

// updateLocale — PATCH /api/v1/me {locale}. ANX-036. Пустая строка сбрасывает
// в «язык браузера» (NULL на backend). Возвращает обновлённого оператора.
export async function updateLocale(client: ApiClient, locale: string): Promise<Operator> {
  const raw = await client.request<{ operator: OperatorPayload }>('/api/v1/me', {
    method: 'PATCH',
    json: { locale },
  });
  return toOperator(raw.operator);
}

// setMyStatus — PATCH /api/v1/me/status {status}. ANX-071. Меняет статус
// присутствия (online/away/busy). Возвращает обновлённого оператора.
export async function setMyStatus(client: ApiClient, status: OperatorStatus): Promise<Operator> {
  const raw = await client.request<{ operator: OperatorPayload }>('/api/v1/me/status', {
    method: 'PATCH',
    json: { status },
  });
  return toOperator(raw.operator);
}

export interface SignupInput {
  email: string;
  password: string;
  tenantName: string;
  // ANX-055: Cloudflare Turnstile response token. Обязателен, если в
  // gateway включён Turnstile-secret; иначе игнорируется.
  turnstileToken?: string;
  // ANX-056a: версия privacy policy, с которой согласился оператор. Backend
  // без этого поля отвечает 400 consent_required.
  consentVersion: string;
}

export interface SignupResult {
  session: AuthSession;
  tenant: { id: string; slug: string; name: string };
  site: { id: string; name: string; widgetKey: string };
}

interface SignupResponse extends AuthResponse {
  tenant: { id: string; slug: string; name: string };
  site: { id: string; name: string; widget_key: string };
}

export async function signup(client: ApiClient, input: SignupInput): Promise<SignupResult> {
  const raw = await client.request<SignupResponse>('/api/v1/auth/signup', {
    method: 'POST',
    auth: false,
    json: {
      email: input.email,
      password: input.password,
      tenant_name: input.tenantName,
      consent_version: input.consentVersion,
      ...(input.turnstileToken ? { turnstile_token: input.turnstileToken } : {}),
    },
  });
  return {
    session: toSession(raw),
    tenant: raw.tenant,
    site: { id: raw.site.id, name: raw.site.name, widgetKey: raw.site.widget_key },
  };
}
